import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";

import Header from "../../components/Header/Header";
import AnalyticsFilterDropdown from "../../components/AnalyticsFilterDropdown/AnalyticsFilterDropdown";
import MetricCard from "../../components/MetricCard/MetricCard";
import PlatformTable from "../../components/PlatformTable/PlatformTable";
import ExportCard from "../../components/ExportCard/ExportCard";
import BottomNav from "../../components/BottomNav/BottomNav";
import AnalyticsGraphs from "../../components/AnalyticsGraphs/AnalyticsGraphs";
import { useNavigate } from "react-router-dom";

import "./AnalyticsPage.css";

const AnalyticsPage = () => {
  
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({ retailers: [], dateRanges: [] });
  const [tableView, setTableView] = useState(false); // pass to AnalyticsGraphs
  const [activeTab, setActiveTab] = useState("analytics");
  const navigate = useNavigate();


const handleTabChange = (tabId) => {
  setActiveTab(tabId); // update local tab state
  if (tabId === "dashboard") navigate("/dashboard");
  else if (tabId === "analytics") navigate("/analytics");
  else if (tabId === "profile") navigate("/profile");
};




  // Load Excel data on mount
  useEffect(() => {
    fetch("../data/posts_data (2).xlsx")
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
        const processedSheet = sheet.map(row => {
        let parsedDate;

        if (typeof row.Date === "number") {
          // Excel serial date (days since 1900-01-01)
          parsedDate = XLSX.SSF.parse_date_code(row.Date);
          parsedDate = new Date(parsedDate.y, parsedDate.m - 1, parsedDate.d);
        } else if (typeof row.Date === "string") {
          // Try parsing as DD/MM/YYYY
          const parts = row.Date.split("/");
          if (parts.length === 3) {
            parsedDate = new Date(+parts[2], parts[1] - 1, +parts[0]);
          } else {
            parsedDate = new Date(row.Date); // fallback
          }
        }

  return {
    ...row,
    Date: parsedDate,
  };
});

        setAllData(processedSheet);
        setFilteredData(processedSheet);
      })
      .catch((err) => console.error("Error loading Excel file:", err));
  }, []);

  // Update filteredData whenever filters change
useEffect(() => {
  let data = [...allData];

  // Retailer filter
  if (filters.retailers.length > 0) {
    data = data.filter((row) => filters.retailers.includes(row.Platform));
  }

  // Date filters
  const hasDateFilters =
    filters.dateRanges.length > 0 || (filters.customDate && filters.customDate.start && filters.customDate.end);

  if (hasDateFilters) {
    data = data.filter((row) => {
      const rowDate = new Date(row.Date);

      // Predefined date ranges
      for (const range of filters.dateRanges) {
        const now = new Date();

        if (range === "Last 7 days") {
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(now.getDate() - 7);
          if (rowDate >= sevenDaysAgo && rowDate <= now) return true;
        }

        if (range === "Last 30 days") {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(now.getDate() - 30);
          if (rowDate >= thirtyDaysAgo && rowDate <= now) return true;
        }

        if (range === "This Month") {
          if (rowDate.getMonth() === now.getMonth() && rowDate.getFullYear() === now.getFullYear()) return true;
        }
      }

      // Custom date range (object)
      if (filters.customDate && filters.customDate.start && filters.customDate.end) {
        const start = new Date(filters.customDate.start);
        const end = new Date(filters.customDate.end);
        if (rowDate >= start && rowDate <= end) return true;
      }

      return false;
    });
  }

  setFilteredData(data);
}, [filters, allData]);


  // Metrics
  const totalViews = filteredData.reduce((acc, row) => acc + (row.Views || 0), 0);
  const totalClicks = filteredData.reduce((acc, row) => acc + (row.Clicks || 0), 0);
  const clickThroughRate = totalViews ? ((totalClicks / totalViews) * 100).toFixed(1) + "%" : "0%";
  const reach = filteredData.length;

  const metrics = [
    { title: "Total Views", value: totalViews },
    { title: "Total Clicks", value: totalClicks },
    { title: "Click-Through Rate", value: clickThroughRate },
    { title: "Reach", value: reach },
  ];

  // Transactions summary
  const transactionsSummary = [
    { title: "Transactions", value: filteredData.reduce((acc, row) => acc + (row.Transactions || 0), 0) },
    { title: "GMV", value: "$" + filteredData.reduce((acc, row) => acc + (row.GMV || 0), 0) },
    { title: "ROAS", value: (filteredData.reduce((acc, row) => acc + (row.ROAS || 0), 0) / (filteredData.length || 1)).toFixed(2) + "x" },
  ];

  // Platform table
  const platformData = filteredData.reduce((acc, row) => {
    const plat = row.Platform;
    if (!acc[plat]) acc[plat] = { platform: plat, posts: 0, views: 0, clicks: 0 };
    acc[plat].posts += 1;
    acc[plat].views += row.Views || 0;
    acc[plat].clicks += row.Clicks || 0;
    return acc;
  }, {});
  const platformDataArray = Object.values(platformData);

  return (
    <div className="phone-frame">
      <div className="dashboard-container">
        <Header username="ERetailer" />

        <div className="dashboard-scroll">
          <AnalyticsFilterDropdown
            onFiltersChange={(appliedFilters) => {
              const retailers = appliedFilters.filter(f => f.type === "Retailer").map(f => f.value);
              const dateRanges = appliedFilters.filter(f => f.type === "Date").map(f => f.value);
              const customDateObj = appliedFilters.find(f => f.type === "CustomDate");
              const customDate = customDateObj ? customDateObj.value : null;
              setFilters({ retailers, dateRanges, customDate });
            }}
          />


          {/* Metrics */}
          <div className="metrics-grid">
            {metrics.map((m, i) => <MetricCard key={i} title={m.title} value={m.value} />)}
          </div>

          {/* Transactions */}
          <div className="transactions-summary">
            {transactionsSummary.map((t, i) => (
              <div key={i} className="transaction-card">
                <h4>{t.title}</h4>
                <p>{t.value}</p>
              </div>
            ))}
          </div>

          {/* Graphs */}
          <AnalyticsGraphs
            data={filteredData}
            tableView={tableView}       // pass state
            setTableView={setTableView} // pass setter
          />

          {/* Platform table */}
          <PlatformTable data={platformDataArray} />

          {/* Export */}
         <ExportCard
            data={filteredData}
            fileName="analytics_data.xlsx"
          />

        </div>

      <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />

      </div>
    </div>
  );
};

export default AnalyticsPage;

